/**
 * DEKO COCKTAILS — Subscription Widget Helper Scripts
 * ====================================================
 * Handles dynamic UI behavior that CSS alone cannot achieve:
 *
 *   1. Subscribe button color toggle (copper → sage)
 *   2. Show/hide trust messaging based on selection
 *   3. Show/hide "Manage Subscription" link
 *   4. Update button text to show price dynamically
 *   5. Hide orphaned FREQUENCY label (JS fallback for :has() unsupported browsers)
 *
 * COMPATIBLE WITH: Seal Subscriptions AND Recharge (auto-detects)
 *
 * IMPORTANT: This script is loaded with a request.design_mode guard in theme.liquid
 * so it NEVER runs inside the Shopify theme customizer.
 *
 * DEPENDENCIES: None (vanilla JS, no jQuery required)
 * COMPATIBILITY: All modern browsers
 */

(function () {
  'use strict';

  // ===== CONFIGURATION =====
  const CONFIG = {
    // Brand colors
    colors: {
      copperGradient: 'linear-gradient(135deg, #B47F5C, #D7A57A)',
      copperHoverGradient: 'linear-gradient(135deg, #946548, #B47F5C)',
      sage: '#004F5C',
      sageHover: '#003D47',
    },

    // Selectors for the main buy button (inside product-form custom element)
    buttonSelectors: [
      'product-form .product-form__submit',
      'product-form button[type="submit"]',
      '.product-form__submit',
      'button[name="add"]',
      '.btn--add-to-cart',
    ].join(', '),

    // Trust messaging element IDs
    trustMessagingId: 'rc-trust-messaging',
    manageLinkId: 'rc-manage-link',

    // Polling config (for waiting for subscription widget to load)
    maxPollAttempts: 50,
    pollInterval: 200, // ms

    // Debounce delay for update checks
    debounceDelay: 100,
  };

  // ===== STATE =====
  let currentMode = 'onetime'; // 'onetime' or 'subscribe'
  let button = null;
  let debounceTimer = null;

  // ===== HELPERS =====

  /**
   * Find the main product form submit button
   */
  function findButton() {
    if (button && document.contains(button)) return button;
    button = document.querySelector(CONFIG.buttonSelectors);
    return button;
  }

  /**
   * Read the current subscription price from the widget or DOM
   * Returns a formatted price string or empty string
   */
  function getSubscribePrice() {
    // Seal Subscriptions: look for price in the subscribe option
    var sealSub = document.querySelector('.sls-purchase-option.sls-subscribe-option.sls-active, .sls-purchase-option.sls-subscribe-option input:checked');
    if (sealSub) {
      var container = sealSub.closest('.sls-purchase-option') || sealSub;
      var priceEl = container.querySelector('.sls-price, [class*="price"]');
      if (priceEl && priceEl.textContent.trim()) return priceEl.textContent.trim();
    }

    // Recharge: try to read from widget's active subscription option
    var rcPriceSelectors = [
      '.rc-option--active .rc-option__price .rc-price--sale',
      '.rc-option--active .rc-option__price strong',
      '.rc-radio--active .rc-radio__price',
    ];
    for (var i = 0; i < rcPriceSelectors.length; i++) {
      try {
        var el = document.querySelector(rcPriceSelectors[i]);
        if (el && el.textContent.trim()) return el.textContent.trim();
      } catch (e) { /* skip */ }
    }

    // Fallback: read from Shopify's price display
    var shopifyPrice = document.querySelector('.price__sale .price-item--sale, .price-item--sale');
    if (shopifyPrice && shopifyPrice.textContent.trim()) return shopifyPrice.textContent.trim();

    return '';
  }

  /**
   * Check if subscription is currently selected
   * Compatible with both Seal Subscriptions and Recharge
   */
  function isSubscriptionSelected() {
    // Strategy 1: Check ALL cart forms for a selling_plan input with a value
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var spInput = forms[i].querySelector('input[name="selling_plan"]');
      if (spInput && spInput.value && spInput.value !== '' && spInput.value !== '0') {
        return true;
      }
    }

    // Strategy 2: Check Seal Subscriptions active state
    var sealActive = document.querySelector('.sls-purchase-option.sls-subscribe-option.sls-active');
    if (sealActive) return true;
    // Also check Seal radio inputs
    var sealRadios = document.querySelectorAll('.sls-purchase-option input[type="radio"]');
    for (var j = 0; j < sealRadios.length; j++) {
      if (sealRadios[j].checked) {
        var parent = sealRadios[j].closest('.sls-purchase-option');
        if (parent && parent.classList.contains('sls-subscribe-option')) return true;
        var text = parent ? parent.textContent.toLowerCase() : '';
        if (text.includes('subscribe') || text.includes('save')) return true;
      }
    }

    // Strategy 3: Check Recharge's data attributes on active option
    var rcSelectors = [
      '.rc-option--active[data-option-type="subscription"]',
      '.rc-radio--active[data-option-type="subscription"]',
      '.rc-radio-group__option--selected[data-option-type="subscription"]',
    ];
    for (var k = 0; k < rcSelectors.length; k++) {
      try {
        if (document.querySelector(rcSelectors[k])) return true;
      } catch (e) { /* skip */ }
    }

    // Strategy 4: Check any radio/input with subscribe-like context
    var allRadios = document.querySelectorAll(
      '.rc-option input[type="radio"], .rc-radio input[type="radio"], ' +
      '#rc_container input[type="radio"], .sls-purchase-options input[type="radio"]'
    );
    for (var m = 0; m < allRadios.length; m++) {
      if (allRadios[m].checked) {
        var radioParent = allRadios[m].closest('.rc-option, .rc-radio, .rc-radio-group__option, .sls-purchase-option');
        if (radioParent) {
          var radioText = radioParent.textContent.toLowerCase();
          if (radioText.includes('subscribe') || radioText.includes('save')) return true;
        }
      }
    }

    return false;
  }

  /**
   * Update the button style based on current selection
   */
  function updateButtonStyle(isSubscribe) {
    const btn = findButton();
    if (!btn) return;

    if (isSubscribe) {
      // Subscription selected → Sage button
      btn.style.setProperty('background', CONFIG.colors.sage, 'important');
      btn.style.setProperty('color', '#fff', 'important');
      btn.setAttribute('data-subscription', 'true');
      btn.classList.add('rc-btn--subscribe');

      // Update button text with dynamic price
      if (!btn.textContent.includes('Subscribe') && !btn.textContent.includes('SUBSCRIBE')) {
        btn.dataset.originalText = btn.textContent;
        var price = getSubscribePrice();
        btn.textContent = price ? 'SUBSCRIBE — ' + price : 'SUBSCRIBE';
      }
    } else {
      // One-time selected → Copper gradient button
      btn.style.setProperty('background', CONFIG.colors.copperGradient, 'important');
      btn.style.setProperty('color', '#fff', 'important');
      btn.removeAttribute('data-subscription');
      btn.classList.remove('rc-btn--subscribe');

      // Restore original text
      if (btn.dataset.originalText) {
        btn.textContent = btn.dataset.originalText;
      } else if (btn.textContent.includes('Subscribe') || btn.textContent.includes('SUBSCRIBE')) {
        btn.textContent = 'ADD TO CART';
      }
    }

    currentMode = isSubscribe ? 'subscribe' : 'onetime';
  }

  /**
   * Toggle trust messaging and manage subscription link visibility
   */
  function updateTrustMessaging(isSubscribe) {
    var trustEl = document.getElementById(CONFIG.trustMessagingId);
    var manageEl = document.getElementById(CONFIG.manageLinkId);

    if (trustEl) {
      trustEl.style.display = isSubscribe ? 'block' : 'none';
    }
    if (manageEl) {
      manageEl.style.display = isSubscribe ? 'block' : 'none';
    }
  }

  /**
   * Add hover effects via JS (for the dynamic sage color)
   */
  function attachHoverEffects() {
    var btn = findButton();
    if (!btn || btn.dataset.hoverAttached) return;

    btn.addEventListener('mouseenter', function () {
      if (currentMode === 'subscribe') {
        btn.style.setProperty('background', CONFIG.colors.sageHover, 'important');
        btn.style.setProperty('box-shadow', '0 4px 12px rgba(0, 79, 92, 0.3)', 'important');
        btn.style.setProperty('transform', 'translateY(-1px)', 'important');
      }
    });

    btn.addEventListener('mouseleave', function () {
      if (currentMode === 'subscribe') {
        btn.style.setProperty('background', CONFIG.colors.sage, 'important');
      } else {
        btn.style.setProperty('background', CONFIG.colors.copperGradient, 'important');
      }
      btn.style.removeProperty('box-shadow');
      btn.style.removeProperty('transform');
    });

    btn.dataset.hoverAttached = 'true';
  }

  /**
   * Main update function — called on every relevant change
   */
  function update() {
    var isSubscribe = isSubscriptionSelected();
    updateButtonStyle(isSubscribe);
    updateTrustMessaging(isSubscribe);
    attachHoverEffects();
  }

  function debouncedUpdate() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(update, CONFIG.debounceDelay);
  }

  /**
   * Hide orphaned FREQUENCY label (JS fallback for browsers without :has() support)
   */
  function hideOrphanedFrequencyLabel() {
    var labels = document.querySelectorAll('label');
    labels.forEach(function (label) {
      var forAttr = (label.getAttribute('for') || '').toLowerCase();
      var text = label.textContent.trim().toLowerCase();

      if (forAttr.includes('frequency') || text === 'frequency' || text === 'frequency:' || text === 'frequency :') {
        var parent = label.parentElement;
        if (parent && parent.children.length === 1) {
          parent.style.display = 'none';
        } else {
          label.style.display = 'none';
        }
      }
    });
  }

  // ===== EVENT LISTENERS =====

  /**
   * Listen for subscription widget changes using lightweight event delegation.
   * Compatible with Seal Subscriptions AND Recharge.
   * NO MutationObserver — prevents Shopify theme customizer freeze.
   */
  function attachListeners() {
    // Listen for clicks on subscription option rows (Seal + Recharge)
    document.addEventListener('click', function (e) {
      var option = e.target.closest(
        // Seal Subscriptions selectors
        '.sls-purchase-option, .sls-purchase-options label, ' +
        '.sls-purchase-options-container, ' +
        // Recharge selectors
        '.rc-option, .rc-radio, .rc-radio-group__option, .rc_option, ' +
        '[data-rc-widget] label, .rc-app-block'
      );
      if (option) {
        // Delay to let the widget update its state first
        setTimeout(update, 50);
        setTimeout(update, 250);
        setTimeout(update, 500);
      }
    });

    // Listen for radio/input changes
    document.addEventListener('change', function (e) {
      // Seal or Recharge radio changes
      if (e.target.type === 'radio' &&
          (e.target.closest('.sls-purchase-option, .sls-purchase-options, .rc-option, .rc-radio, .rc-radio-group__option, #rc_container, [data-rc-widget]') ||
           e.target.name === 'selling_plan' || e.target.name === 'purchase_option')) {
        setTimeout(update, 50);
        setTimeout(update, 250);
      }

      // Hidden selling_plan input changes
      if (e.target.name === 'selling_plan') {
        setTimeout(update, 50);
      }
    });

    // Recharge custom events
    document.addEventListener('recharge:widget:update', debouncedUpdate);
    document.addEventListener('recharge:subscription:changed', debouncedUpdate);

    // Lightweight polling for selling_plan input changes across ALL forms
    // (replaces the heavy MutationObserver that froze the customizer)
    var lastSellingPlan = '';
    setInterval(function () {
      var val = '';
      var forms = document.querySelectorAll('form[action*="/cart/add"]');
      for (var i = 0; i < forms.length; i++) {
        var input = forms[i].querySelector('input[name="selling_plan"]');
        if (input && input.value) {
          val = input.value;
          break;
        }
      }
      if (val !== lastSellingPlan) {
        lastSellingPlan = val;
        update();
      }
    }, 500);
  }

  // ===== INITIALIZATION =====

  function init() {
    // ONLY run on product pages — skip homepage and all other pages
    if (!document.querySelector('.product-form, form[action*="/cart/add"]')) {
      return;
    }

    hideOrphanedFrequencyLabel();
    attachListeners();

    // Poll until subscription widget is loaded (Seal or Recharge, both load async)
    var attempts = 0;
    var poll = setInterval(function () {
      attempts++;
      var widget = document.querySelector(
        // Seal Subscriptions
        '.sls-purchase-options, .sls-purchase-options-container, ' +
        // Recharge
        '.rc-option, .rc-widget, .rc-container, recharge-subscription-widget, #rc_container, .rc-app-block'
      );
      if (widget || attempts >= CONFIG.maxPollAttempts) {
        clearInterval(poll);
        if (widget) {
          update();
          hideOrphanedFrequencyLabel();
        }
      }
    }, CONFIG.pollInterval);

    // Also run on initial load
    update();
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
